import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  message,
  Space,
  Typography,
  Radio,
  Progress,
  Drawer,
  Descriptions,
  Tag,
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { saveAs } from 'file-saver';

const { Text } = Typography;

const BASE_URL = 'https://ds2.cn/mp';
const API = '/api/v1/meme-pet';

interface BeastType {
  id: number;
  code: string;
  speciesId?: string;
  name: string;
  description: string;
  emoji?: string;
  level: string;
  beastClass?: string;
  beastType?: string;
  baseHp: number;
  baseAttack: number;
  baseDefense: number;
  baseSpeed: number;
  sleepDurationMinutes?: number;
  exploreDurationMinutes?: number;
  preferredTerrain?: string;
  terrainForest?: number;
  terrainSwamp?: number;
  terrainDesert?: number;
  terrainCoast?: number;
  terrainVolcanic?: number;
  satietyDrainMultiplier?: number;
  primaryFood?: string;
  bodySize?: string;
  textureBaby?: string;
  textureJuvenile?: string;
  textureAdult?: string;
  textureBeast?: string;
  talentRange?: string;
  terrainTalentRange?: string;
  combatLoot?: string;
  growthStages?: string;
  hatchPool?: string;
  created: string;
  updated: string;
  instanceCount?: number;
}

const BeastTab: React.FC = () => {
  const [types, setTypes] = useState<BeastType[]>([]);
  const [loading, setLoading] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<BeastType | null>(null);

  // 详情抽屉
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<BeastType | null>(null);

  const [batchProgress, setBatchProgress] = useState({
    currentType: '',
    currentTypeIndex: 0,
    totalTypes: 0,
    status: 'idle' as 'idle' | 'running' | 'completed' | 'error',
    completedTypes: [] as string[],
    failedTypes: [] as { code: string; error: string }[],
  });

  const [generateForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types`);
      const data = await res.json();
      if (data.success) setTypes(data.data || []);
      else message.error(data.message || '获取Meme Pet种类失败');
    } catch { message.error('获取Meme Pet种类失败'); }
    finally { setLoading(false); }
  };

  const generateInstances = async (values: any) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: selectedType?.code, quantity: values.quantity }),
      });
      const data = await res.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个实例`);
        const instances = data.data.instances as { hash: string }[];
        const content = instances.map(i => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, `${selectedType?.speciesId || selectedType?.code}.txt`);
        setGenerateModalVisible(false);
        generateForm.resetFields();
        fetchTypes();
      } else { message.error(data.message || '生成失败'); }
    } catch { message.error('生成失败'); }
    finally { setLoading(false); }
  };

  const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3, retryDelay = 1000): Promise<Response> => {
    let lastError: Error | null = null;
    for (let i = 0; i <= maxRetries; i++) {
      try {
        const res = await fetch(url, options);
        if (res.ok) return res;
        if (res.status >= 400 && res.status < 500) return res;
        throw new Error(`Server error: ${res.status}`);
      } catch (err: any) {
        lastError = err;
        if (i === maxRetries) break;
        await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, i)));
      }
    }
    throw lastError || new Error('请求失败');
  };

  const downloadAsZip = async (files: { name: string; content: string }[]) => {
    try {
      setLoading(true);
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      files.forEach(f => zip.file(`${f.name}.txt`, `﻿${f.content}`, { binary: false }));
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `ancient_beast_batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`);
      message.success(`成功生成ZIP文件包含 ${files.length} 个文件`);
    } catch (err) { message.error('生成ZIP文件失败'); console.error(err); }
    finally { setLoading(false); }
  };

  const batchGenerateInstances = async (values: any) => {
    const { quantity, downloadType } = values;
    setBatchProgress({ currentType: '', currentTypeIndex: 0, totalTypes: types.length, status: 'running', completedTypes: [], failedTypes: [] });
    setLoading(true);
    try {
      const allFiles: { name: string; content: string }[] = [];
      for (let i = 0; i < types.length; i++) {
        const t = types[i];
        setBatchProgress(p => ({ ...p, currentType: t.code, currentTypeIndex: i + 1 }));
        try {
          const res = await fetchWithRetry(`${API}/types/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: t.code, quantity }),
          }, 3, 1000);
          const data = await res.json();
          if (data.success && data.data?.instances) {
            const content = data.data.instances.map((i: any) => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
            const fileName = t.speciesId || t.code;
            allFiles.push({ name: fileName, content });
            setBatchProgress(p => ({ ...p, completedTypes: [...p.completedTypes, t.code] }));
            if (downloadType === 'batch') {
              const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
              saveAs(new Blob([BOM, content], { type: 'text/plain;charset=utf-8' }), `${t.speciesId || t.code}.txt`);
              await new Promise(r => setTimeout(r, 500));
            }
          } else { throw new Error(data.message || '生成失败'); }
        } catch (err: any) {
          setBatchProgress(p => ({ ...p, failedTypes: [...p.failedTypes, { code: t.code, error: err.message || '未知错误' }] }));
        }
      }
      if (downloadType === 'zip' && allFiles.length > 0) await downloadAsZip(allFiles);
      setBatchProgress(p => ({ ...p, status: 'completed' }));
      const successCount = allFiles.length;
      message.success(`批量生成完成：成功 ${successCount} 个`);
      fetchTypes();
    } catch (err) { message.error('批量生成失败'); setBatchProgress(p => ({ ...p, status: 'error' })); }
    finally { setLoading(false); }
  };

  const renderLevel = (v: string) => {
    const colors: Record<string, string> = { UR: 'red', SSR: 'gold', SR: 'purple', QR: 'default' };
    return <Tag color={colors[v] || 'default'}>{v}</Tag>;
  };

  const fmtNum = (v?: number) => (v === null || v === undefined ? '—' : v);

  const columns = [
    { title: '卡牌编号', dataIndex: 'speciesId', key: 'speciesId', width: 130, fixed: 'left' as const },
    { title: '编码', dataIndex: 'code', key: 'code', width: 120, fixed: 'left' as const },
    { title: '名称', dataIndex: 'name', key: 'name', width: 110, fixed: 'left' as const },
    { title: 'emoji', dataIndex: 'emoji', key: 'emoji', width: 60 },
    {
      title: '稀有度', dataIndex: 'level', key: 'level', width: 80,
      render: renderLevel,
    },
    { title: '类别', dataIndex: 'beastClass', key: 'beastClass', width: 70 },
    { title: 'type', dataIndex: 'beastType', key: 'beastType', width: 70 },
    { title: 'HP', dataIndex: 'baseHp', key: 'baseHp', width: 60 },
    { title: 'ATK', dataIndex: 'baseAttack', key: 'baseAttack', width: 60 },
    { title: 'DEF', dataIndex: 'baseDefense', key: 'baseDefense', width: 60 },
    { title: 'SPD', dataIndex: 'baseSpeed', key: 'baseSpeed', width: 60 },
    { title: '睡眠', dataIndex: 'sleepDurationMinutes', key: 'sleep', width: 70, render: fmtNum },
    { title: '探索', dataIndex: 'exploreDurationMinutes', key: 'explore', width: 70, render: fmtNum },
    { title: '偏好地形', dataIndex: 'preferredTerrain', key: 'preferredTerrain', width: 90 },
    { title: '森林', dataIndex: 'terrainForest', key: 'tF', width: 60, render: fmtNum },
    { title: '沼泽', dataIndex: 'terrainSwamp', key: 'tS', width: 60, render: fmtNum },
    { title: '沙漠', dataIndex: 'terrainDesert', key: 'tD', width: 60, render: fmtNum },
    { title: '海岸', dataIndex: 'terrainCoast', key: 'tC', width: 60, render: fmtNum },
    { title: '火山', dataIndex: 'terrainVolcanic', key: 'tV', width: 60, render: fmtNum },
    { title: '主食', dataIndex: 'primaryFood', key: 'primaryFood', width: 140, ellipsis: true },
    { title: '体型', dataIndex: 'bodySize', key: 'bodySize', width: 90 },
    {
      title: '实例数', dataIndex: 'instanceCount', key: 'instanceCount', width: 80,
      render: (n: number) => n || 0,
    },
    {
      title: '操作', key: 'action', width: 150, fixed: 'right' as const,
      render: (_: any, record: BeastType) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />}
            onClick={() => { setDetailRecord(record); setDetailVisible(true); }}>
            详情
          </Button>
          <Button type="link" size="small"
            onClick={() => { setSelectedType(record); setGenerateModalVisible(true); }}>
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  // 美化 JSON 显示
  const renderJson = (s?: string | null) => {
    if (!s) return <Text type="secondary">—</Text>;
    try {
      const obj = JSON.parse(s);
      return <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(obj, null, 2)}</pre>;
    } catch {
      return <pre style={{ margin: 0, fontSize: 12 }}>{s}</pre>;
    }
  };

  return (
    <Card
      bordered={false}
      extra={
        <Space>
          <Button type="primary" onClick={() => setBatchGenerateModalVisible(true)}>批量生成</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchTypes}>刷新</Button>
        </Space>
      }
    >
      <Table columns={columns} dataSource={types} rowKey="code" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 2400 }} />

      {/* 生成实例 Modal */}
      <Modal title={`生成实例 - ${selectedType?.name}`} open={generateModalVisible}
        onOk={() => generateForm.submit()}
        onCancel={() => { setGenerateModalVisible(false); generateForm.resetFields(); }}>
        <Form form={generateForm} layout="vertical" onFinish={generateInstances}>
          <Form.Item name="quantity" label="生成数量" initialValue={100} rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Text type="secondary">生成后下载 txt（{BASE_URL}/&lt;hash&gt;）</Text>
        </Form>
      </Modal>

      {/* 批量生成 Modal */}
      <Modal title="批量生成Meme Pet实例" open={batchGenerateModalVisible} footer={null} width={600}
        onCancel={() => { setBatchGenerateModalVisible(false); batchGenerateForm.resetFields(); }}>
        <Form form={batchGenerateForm} layout="vertical" onFinish={batchGenerateInstances}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">将为所有种类（共 {types.length} 个）各生成指定数量的实例</Text>
          </div>
          <Form.Item name="quantity" label="每种生成数量" rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} disabled={loading} />
          </Form.Item>
          <Form.Item name="downloadType" label="下载方式" initialValue="batch" rules={[{ required: true }]}>
            <Radio.Group disabled={loading}>
              <Radio value="batch">即时下载</Radio>
              <Radio value="zip">ZIP 打包</Radio>
            </Radio.Group>
          </Form.Item>
          {batchProgress.status !== 'idle' && (
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Text strong>生成进度</Text>
              <div style={{ marginBottom: 8 }}>
                {batchProgress.status === 'running' && <Text>正在处理: {batchProgress.currentType}</Text>}
                {batchProgress.status === 'completed' && <Text>✓ 完成</Text>}
                {batchProgress.status === 'error' && <Text type="danger">✗ 出错</Text>}
              </div>
              <Progress percent={batchProgress.totalTypes > 0 ? Math.round((batchProgress.currentTypeIndex / batchProgress.totalTypes) * 100) : 0}
                status={batchProgress.status === 'completed' ? 'success' : batchProgress.status === 'error' ? 'exception' : 'active'}
                format={() => `${batchProgress.currentTypeIndex}/${batchProgress.totalTypes}`} />
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <Text type="success">已完成: {batchProgress.completedTypes.length} 个</Text>
                {batchProgress.failedTypes.length > 0 && (
                  <><br /><Text type="danger">失败: {batchProgress.failedTypes.length} 个</Text></>
                )}
              </div>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} disabled={batchProgress.status === 'running'} icon={<DownloadOutlined />}>
                {loading ? '生成中...' : '开始批量生成'}
              </Button>
              <Button onClick={() => { setBatchGenerateModalVisible(false); batchGenerateForm.resetFields(); setBatchProgress(p => ({ ...p, status: 'idle' })); }}>关闭</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情抽屉 —— 显示所有字段 + JSON */}
      <Drawer
        title={detailRecord ? `${detailRecord.emoji || ''} ${detailRecord.name} · ${detailRecord.code}` : '详情'}
        open={detailVisible}
        width={720}
        onClose={() => setDetailVisible(false)}
      >
        {detailRecord && (
          <Descriptions column={2} size="small" bordered labelStyle={{ width: 110 }}>
            <Descriptions.Item label="卡牌编号">{detailRecord.speciesId || '—'}</Descriptions.Item>
            <Descriptions.Item label="编码">{detailRecord.code}</Descriptions.Item>
            <Descriptions.Item label="名称">{detailRecord.name}</Descriptions.Item>
            <Descriptions.Item label="emoji">{detailRecord.emoji || '—'}</Descriptions.Item>
            <Descriptions.Item label="稀有度">{renderLevel(detailRecord.level)}</Descriptions.Item>
            <Descriptions.Item label="类别">{detailRecord.beastClass}</Descriptions.Item>
            <Descriptions.Item label="type 标记">{detailRecord.beastType || '—'}</Descriptions.Item>
            <Descriptions.Item label="基础 HP">{detailRecord.baseHp}</Descriptions.Item>
            <Descriptions.Item label="基础攻击">{detailRecord.baseAttack}</Descriptions.Item>
            <Descriptions.Item label="基础防御">{detailRecord.baseDefense}</Descriptions.Item>
            <Descriptions.Item label="基础速度">{detailRecord.baseSpeed}</Descriptions.Item>
            <Descriptions.Item label="睡眠分钟">{fmtNum(detailRecord.sleepDurationMinutes)}</Descriptions.Item>
            <Descriptions.Item label="探索分钟">{fmtNum(detailRecord.exploreDurationMinutes)}</Descriptions.Item>
            <Descriptions.Item label="偏好地形">{detailRecord.preferredTerrain || '—'}</Descriptions.Item>
            <Descriptions.Item label="饱食流失倍率">{fmtNum(detailRecord.satietyDrainMultiplier)}</Descriptions.Item>
            <Descriptions.Item label="森林">{fmtNum(detailRecord.terrainForest)}</Descriptions.Item>
            <Descriptions.Item label="沼泽">{fmtNum(detailRecord.terrainSwamp)}</Descriptions.Item>
            <Descriptions.Item label="沙漠">{fmtNum(detailRecord.terrainDesert)}</Descriptions.Item>
            <Descriptions.Item label="海岸">{fmtNum(detailRecord.terrainCoast)}</Descriptions.Item>
            <Descriptions.Item label="火山">{fmtNum(detailRecord.terrainVolcanic)}</Descriptions.Item>
            <Descriptions.Item label="主食">{detailRecord.primaryFood || '—'}</Descriptions.Item>
            <Descriptions.Item label="体型">{detailRecord.bodySize || '—'}</Descriptions.Item>
            <Descriptions.Item label="实例数">{detailRecord.instanceCount ?? 0}</Descriptions.Item>
            <Descriptions.Item label="图片 baby" span={2}>{detailRecord.textureBaby || '—'}</Descriptions.Item>
            <Descriptions.Item label="图片 juvenile" span={2}>{detailRecord.textureJuvenile || '—'}</Descriptions.Item>
            <Descriptions.Item label="图片 adult" span={2}>{detailRecord.textureAdult || '—'}</Descriptions.Item>
            <Descriptions.Item label="图片 beast" span={2}>{detailRecord.textureBeast || '—'}</Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{detailRecord.description || '—'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="talent_range" span={2}>{renderJson(detailRecord.talentRange)}</Descriptions.Item>
            <Descriptions.Item label="terrain_talent_range" span={2}>{renderJson(detailRecord.terrainTalentRange)}</Descriptions.Item>
            <Descriptions.Item label="combat_loot" span={2}>{renderJson(detailRecord.combatLoot)}</Descriptions.Item>
            <Descriptions.Item label="growth_stages" span={2}>{renderJson(detailRecord.growthStages)}</Descriptions.Item>
            <Descriptions.Item label="hatch_pool" span={2}>{renderJson(detailRecord.hatchPool)}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
};

export default BeastTab;
